import React, { useEffect, useId, useRef, useState } from "react";
import { CalendarClock, Loader2, Trash2, X } from "lucide-react";

import Modal from "../../components/ui/Modal";
import { useMe } from "../../hooks/queries/useMe";
import { useHasFeature } from "../../hooks/queries/usePlans";
import UpgradePrompt from "../../components/plans/UpgradePrompt";
import { isPlanRequired } from "../../constants/plans";
import {
  useCreateReportSchedule,
  useDeleteReportSchedule,
  useReportSchedules,
  useUpdateReportSchedule,
} from "./api";

const WEEKDAYS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const HOURS = Array.from({ length: 18 }, (_, i) => i + 5); // 5:00 – 22:00

const describe = (schedule) => {
  const when =
    schedule.frequency === "weekly"
      ? `Cada ${WEEKDAYS[schedule.weekday]}`
      : `El día ${schedule.day_of_month} de cada mes`;
  const period = schedule.frequency === "weekly" ? "últimos 7 días" : "últimos 28 días";
  return `${when} a las ${schedule.hour}:00 · ${period}`;
};

/** Does a stored schedule belong to the report the user is looking at? */
const sameReport = (schedule, reportId, params) =>
  schedule.report_id === reportId && (!params.athleteId || schedule.params?.athleteId === params.athleteId);

const fieldClass =
  "w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-navy-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-200";

/**
 * Scheduled email delivery for one report. Recipients are never typed: the
 * report goes to the account's own address (plus, for athletes, their
 * guardian), because it carries minors' self-reported health data.
 */
const ScheduleModal = ({ isOpen, onClose, reportId, reportTitle, params, allowGuardian }) => {
  const titleId = useId();
  const me = useMe();
  const schedulesQuery = useReportSchedules();
  const createSchedule = useCreateReportSchedule();
  const updateSchedule = useUpdateReportSchedule();
  const deleteSchedule = useDeleteReportSchedule();

  const [frequency, setFrequency] = useState("weekly");
  const [weekday, setWeekday] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [hour, setHour] = useState(7);
  const [includeGuardian, setIncludeGuardian] = useState(false);

  // Weekly delivery for athletes is a Pro feature (DEV-43). UX only: the
  // backend refuses it with plan_required either way.
  const hasWeekly = useHasFeature("weekly_reports");
  const weeklyLocked = me.data?.role === "athlete" && !hasWeekly;
  const touched = useRef(false);
  useEffect(() => {
    if (weeklyLocked && !touched.current) setFrequency("monthly");
  }, [weeklyLocked]);

  const email = me.data?.user?.email;
  const existing = (schedulesQuery.data ?? []).filter((s) => sameReport(s, reportId, params));
  const error = createSchedule.error || updateSchedule.error || deleteSchedule.error;

  const submit = (event) => {
    event.preventDefault();
    createSchedule.mutate({
      report_id: reportId,
      params: params.athleteId ? { athleteId: params.athleteId } : {},
      frequency,
      weekday: frequency === "weekly" ? weekday : null,
      day_of_month: frequency === "monthly" ? dayOfMonth : null,
      hour,
      include_guardian: allowGuardian && includeGuardian,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} labelledById={titleId} className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
      <div className="flex items-start justify-between gap-3 border-b border-neutral-200 px-5 py-4">
        <div>
          <h2 id={titleId} className="font-display text-2xl font-semibold text-navy-900">
            Programar envío
          </h2>
          <p className="text-[13px] text-ink">{reportTitle} en PDF, por correo.</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="grid h-9 w-9 place-items-center rounded-lg text-ink hover:bg-paper"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
        {existing.length > 0 && (
          <section className="mb-5">
            <h3 className="mb-2 text-[12px] font-semibold tracking-wide text-peri-300 uppercase">Envíos activos</h3>
            <ul className="space-y-2">
              {existing.map((schedule) => (
                <li key={schedule.id} className="flex items-center gap-3 rounded-lg border border-neutral-200 px-3 py-2.5">
                  <CalendarClock size={18} className="shrink-0 text-navy-500" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm ${schedule.active ? "text-navy-900" : "text-peri-300 line-through"}`}>
                      {describe(schedule)}
                    </p>
                    {schedule.include_guardian && <p className="text-[12px] text-ink">Incluye a tu acudiente</p>}
                  </div>
                  <label className="flex items-center gap-1.5 text-[12px] text-ink">
                    <input
                      type="checkbox"
                      checked={schedule.active}
                      onChange={(event) => updateSchedule.mutate({ id: schedule.id, active: event.target.checked })}
                      className="h-4 w-4 accent-navy-700"
                    />
                    Activo
                  </label>
                  <button
                    type="button"
                    onClick={() => deleteSchedule.mutate(schedule.id)}
                    aria-label={`Eliminar envío: ${describe(schedule)}`}
                    className="grid h-8 w-8 place-items-center rounded-md text-alert-300 hover:bg-red-50"
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <form onSubmit={submit} className="space-y-4">
          <fieldset>
            <legend className="mb-1.5 text-sm font-medium text-navy-900">Frecuencia</legend>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["weekly", "Semanal", "Cubre los últimos 7 días"],
                ["monthly", "Mensual", "Cubre los últimos 28 días"],
              ].map(([value, label, hint]) => (
                <label
                  key={value}
                  className={`cursor-pointer rounded-lg border px-3 py-2.5 ${
                    frequency === value ? "border-navy-700 bg-peri-100" : "border-neutral-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="frequency"
                    value={value}
                    checked={frequency === value}
                    onChange={() => {
                      touched.current = true;
                      setFrequency(value);
                    }}
                    className="sr-only"
                  />
                  <span className="block text-sm font-semibold text-navy-900">{label}</span>
                  <span className="block text-[12px] text-ink">
                    {value === "weekly" && weeklyLocked ? "Incluido en el plan Pro" : hint}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid grid-cols-2 gap-3">
            {frequency === "weekly" ? (
              <label className="text-sm text-navy-900">
                Día
                <select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} className={`${fieldClass} mt-1`}>
                  {WEEKDAYS.map((name, index) => (
                    <option key={name} value={index}>
                      {name.charAt(0).toUpperCase() + name.slice(1)}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="text-sm text-navy-900">
                Día del mes
                <select value={dayOfMonth} onChange={(e) => setDayOfMonth(Number(e.target.value))} className={`${fieldClass} mt-1`}>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="text-sm text-navy-900">
              Hora (Colombia)
              <select value={hour} onChange={(e) => setHour(Number(e.target.value))} className={`${fieldClass} mt-1`}>
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {h}:00
                  </option>
                ))}
              </select>
            </label>
          </div>

          {allowGuardian && (
            <label className="flex items-start gap-2 text-sm text-navy-900">
              <input
                type="checkbox"
                checked={includeGuardian}
                onChange={(event) => setIncludeGuardian(event.target.checked)}
                className="mt-0.5 h-4 w-4 accent-navy-700"
              />
              <span>
                Enviar también a mi acudiente
                <span className="block text-[12px] text-ink">Al correo de acudiente registrado en tu perfil.</span>
              </span>
            </label>
          )}

          <p className="rounded-lg bg-paper px-3 py-2 text-[12.5px] text-ink">
            Se enviará a <b className="text-navy-900">{email || "tu correo"}</b>. Solo puedes recibir reportes en el correo de
            tu cuenta.
          </p>

          {frequency === "weekly" && weeklyLocked && !error && (
            <UpgradePrompt
              role="athlete"
              message="El reporte semanal automático está incluido en el plan Pro. Con tu plan actual puedes programar el reporte mensual."
            />
          )}
          {error && isPlanRequired(error) && <UpgradePrompt error={error} />}
          {error && !isPlanRequired(error) && (
            <p role="alert" className="text-sm text-alert-300">
              {error.message}
            </p>
          )}
          {createSchedule.isSuccess && !createSchedule.isPending && (
            <p role="status" className="text-sm text-[#0f8a6a]">
              Envío programado.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-ink hover:bg-paper">
              Cerrar
            </button>
            <button
              type="submit"
              disabled={createSchedule.isPending || (frequency === "weekly" && weeklyLocked)}
              className="flex items-center gap-2 rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-700 disabled:opacity-60"
            >
              {createSchedule.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
              Programar
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ScheduleModal;
